import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { CompanyLogo } from "../../components/CompanyLogo";
import { ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { useHiringSummary } from "../../lib/api";
import { fmtValue } from "../../lib/format";
import { RequestsSection, UpcomingInterviews } from "./HiringExtras";
import { SectionCard, pillClass, useDateFormat } from "./shared";

/** The hiring pipeline as HR reads it from the lifecycle: openings, interviews, offers, staffing plans.
 *
 * Kept distinct from `/people/recruitment`, which is the *working* screen — templates, screening, shortlists and
 * the human decision. This one is the state of play, and it carries the three things the recruitment screen does
 * not show at all: scheduled interviews, issued offers and the staffing plans behind the vacancies. */
export default function Hiring({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useHiringSummary(company || undefined);
  const fmtDate = useDateFormat();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-40" />)}</div>;
  const d = q.data;
  const summary = `${d.applicants_total} ${t("hr.applicants")} · ${Object.entries(d.applicants_by_status).map(([k, v]) => `${k} ${v}`).join(" · ")}`;

  return (
    <div className="mt-4 space-y-4" data-hr-hiring>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Link to="/people/recruitment" className="text-xs font-semibold text-brand-primary hover:underline">{t("hr.toRecruitment")}</Link>
        <FreshnessStamp lastUpdated={d.as_of} />
      </div>

      <SectionCard title={t("hr.recruitment")} aside={summary}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("hr.opening")}</th>
                <th className="px-4 py-2">{t("exec.division")}</th>
                <th className="px-2 py-2 text-right">{t("hr.vacancies")}</th>
                <th className="px-2 py-2 text-right">{t("hr.applicants")}</th>
                <th className="px-4 py-2">{t("hr.pipeline")}</th>
                <th className="px-4 py-2">{t("ops.status")}</th>
              </tr>
            </thead>
            <tbody>
              {d.openings.map((o) => (
                <tr key={o.name} className="border-t border-brand-grey-light">
                  <td className="px-4 py-2">
                    <div className="font-semibold">{o.job_title}</div>
                    <div className="text-[11px] text-brand-grey">{o.designation} · {fmtDate(o.posted_on)} · {fmtValue(o.lower_range, "CDF", lng)}–{fmtValue(o.upper_range, "CDF", lng)}</div>
                  </td>
                  <td className="px-4 py-2"><CompanyLogo company={o.company} size={14} showName className="text-xs" /></td>
                  <td className="px-2 py-2 text-right">{o.planned_vacancies}</td>
                  <td className="px-2 py-2 text-right font-semibold">{o.applicants}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(o.by_status).map(([s, n]) => <span key={s} className={pillClass(s)}>{t(`hr.applicantStatus.${s}`, s)} {n}</span>)}
                    </div>
                  </td>
                  <td className="px-4 py-2"><span className={pillClass(o.status)}>{t(`hr.openingStatus.${o.status}`, o.status ?? "")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-1 gap-4 border-t border-brand-grey-light p-4 text-sm md:grid-cols-2">
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-brand-grey">{t("hr.interviews")}</div>
            {d.interviews.slice(0, 8).map((i) => (
              <div key={i.name} className="flex items-center justify-between gap-2 py-0.5">
                <span className="truncate">{i.applicant_name} <span className="text-xs text-brand-grey">· {i.interview_type} · {fmtDate(i.scheduled_on)}</span></span>
                <span className={pillClass(i.status)}>{t(`hr.interviewStatus.${i.status}`, i.status)}</span>
              </div>
            ))}
          </div>
          <div>
            <div className="mb-1 text-[11px] uppercase tracking-wide text-brand-grey">{t("hr.offers")}</div>
            {d.offers.map((o) => (
              <div key={o.name} className="flex items-center justify-between gap-2 py-0.5">
                <span className="truncate">{o.applicant_name} <span className="text-xs text-brand-grey">· {o.designation} · {o.company} · {fmtDate(o.offer_date)}</span></span>
                <span className={pillClass(o.status)}>{t(`hr.offerStatus.${o.status}`, o.status ?? "")}</span>
              </div>
            ))}
            {d.staffing_plans.map((s) => (
              <div key={s.name} className="mt-2 text-xs text-brand-grey">
                {t("hr.staffingPlan")}: {s.name} · {fmtDate(s.from_date)} → {fmtDate(s.to_date)} · {fmtValue(s.total_estimated_budget, "CDF", lng)}
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
      <RequestsSection company={company} />
      <UpcomingInterviews company={company} />
    </div>
  );
}
